// Headless chart-export infrastructure.
// Spec: .aidev/tasks/headless-charts-sdk-spike/docs/architecture-final.md §3–§4
//
// One module holds the whole contract: ExportContext, the chart registry,
// the SVG→PNG rasterizer (ported 1:1 from legacy page_lib.svgToCanvas for
// pixel-parity), and the useExportableChart hook. The hook is a no-op on the
// live stats page (no ExportContext provider) and activates only under RenderShell.

import { createContext, useContext, useEffect } from 'react'
import type { ComponentType } from 'react'

// ---------------------------------------------------------------------------
// SVG → PNG rasterization helper
// ---------------------------------------------------------------------------

// Direct port of legacy page_lib.coffee `svgToCanvas`: SVG → base64 → <img> →
// drawImage(canvas) → toDataURL('image/png'). The browser's Skia rasterizer is
// load-bearing for pixel-parity with the existing pptx.coffee export pipeline —
// do not swap for an external SVG converter without explicit approval.
export function svgToPngDataUrl(svgString: string, width: number, height: number): Promise<string> {
  return new Promise((resolve, reject) => {
    const svg64 = btoa(unescape(encodeURIComponent(svgString)))
    const img = new Image()
    img.crossOrigin = 'anonymous'
    img.onload = () => {
      const canvas = document.createElement('canvas')
      canvas.width = width
      canvas.height = height
      const ctx = canvas.getContext('2d', { willReadFrequently: true })!
      ctx.drawImage(img, 0, 0)
      resolve(canvas.toDataURL('image/png'))
    }
    img.onerror = () => reject(new Error('SVG rasterization failed'))
    img.src = 'data:image/svg+xml;base64,' + svg64
  })
}

// ---------------------------------------------------------------------------
// ExportContext
// ---------------------------------------------------------------------------

// Default `{ requestId: null }` signals "not in export mode". Only RenderShell
// provides a non-null requestId; the hook treats that as activation.
export const ExportContext = createContext<{ requestId: string | null }>({
  requestId: null
})

// ---------------------------------------------------------------------------
// ExportableCharts registry
// ---------------------------------------------------------------------------

export interface ExportableChart {
  // The existing chart component — never a parallel "*Exportable" duplicate.
  Component: ComponentType<any>
  // A family whose sub-view is drawn by a different component resolves it from the payload,
  // instead of registering a dispatcher component whose only job is to switch. `Component`
  // stays the default the family falls back to. It cannot be folded into `Component` itself:
  // a resolver and a function component are both `typeof 'function'`, so there would be
  // nothing to tell them apart.
  resolveComponent?: (data: any, params: any) => ComponentType<any>
  // Adapts a setChartExportData payload into the props the live component expects.
  toProps: (data: unknown, params: any) => Record<string, unknown>
}

class ExportableChartsRegistry {
  private charts = new Map<string, ExportableChart>()

  register(chartType: string, entry: ExportableChart): void {
    this.charts.set(chartType, entry)
  }

  get(chartType: string): ExportableChart | undefined {
    return this.charts.get(chartType)
  }

  has(chartType: string): boolean {
    return this.charts.has(chartType)
  }
}

export const ExportableCharts = new ExportableChartsRegistry()

// ---------------------------------------------------------------------------
// chartReady emitter
// ---------------------------------------------------------------------------

export type ChartReadyDetail = {
  requestId: string
  outputCount?: number
  error?: string
}

// One requestId gets exactly one answer. The set is written synchronously at
// dispatch time so a later emitter (watchdog, onerror bridge, React 17's dev
// re-dispatch of a boundary-caught error) can never contradict the first one.
const settledRequests = new Set<string>()

export function emitChartReady(detail: ChartReadyDetail): boolean {
  if (settledRequests.has(detail.requestId)) return false

  settledRequests.add(detail.requestId)
  window.dispatchEvent(new CustomEvent('aytm:chartReady', { detail }))

  return true
}

export function isChartReadySettled(requestId: string): boolean {
  return settledRequests.has(requestId)
}

// Test-only reset — the registry is module-level and outlives a jsdom instance.
export function resetChartReadySettled(): void {
  settledRequests.clear()
}

// ---------------------------------------------------------------------------
// Export debug snapshot
// ---------------------------------------------------------------------------

// A page can mount several hook instances (an inert error-emitter dispatcher plus
// the child chart that owns the contract), so the snapshot is a list keyed by
// label — a single object would let the last commit hide whoever actually stalled.
export type ExportDebugEntry = { label: string; [k: string]: unknown }

export function recordExportDebug(entry: ExportDebugEntry): void {
  const store = window as any
  if (!Array.isArray(store.__aytmExportDebug)) store.__aytmExportDebug = []

  const list: ExportDebugEntry[] = store.__aytmExportDebug
  const index = list.findIndex(item => item.label === entry.label)

  if (index === -1) list.push(entry)
  else list[index] = { ...list[index], ...entry }
}

export function readExportDebug(): ExportDebugEntry[] {
  const list = (window as any).__aytmExportDebug
  return Array.isArray(list) ? list : []
}

export function clearExportDebug(): void {
  ;(window as any).__aytmExportDebug = []
}

// ---------------------------------------------------------------------------
// useExportableChart hook
// ---------------------------------------------------------------------------

type UseExportableOpts = {
  // Per-chart readiness signals; all must be truthy before rasterization.
  deps: unknown[]
  // Async because rasterization runs through <img>.onload → canvas.
  prepareForExport: () => Promise<string[]>
  onError?: (err: Error) => void
  // Names this instance in the debug snapshot the watchdog reports.
  debugLabel?: string
}

export function useExportableChart({ deps, prepareForExport, onError, debugLabel }: UseExportableOpts) {
  const { requestId } = useContext(ExportContext)
  const depsReady = deps.every(d => Boolean(d))

  // Headless only: on the live page there is no requestId and no watchdog to read this.
  if (requestId) {
    recordExportDebug({
      label: debugLabel || 'chart',
      requestId,
      deps: deps.map(d => Boolean(d)),
      depsReady
    })
  }

  useEffect(() => {
    // Live page: no requestId → no-op. Render-route: full flow.
    if (!requestId) return
    if (!depsReady) return

    let cancelled = false

    // rAF guarantees React commit + browser paint finished before rasterization.
    const handle = requestAnimationFrame(async () => {
      try {
        // Expose a lazy accessor first; warm-rasterize, then re-point it at the
        // cached PNGs so the driver's getChartExportPng() returns instantly.
        ;(window as any).getChartExportPng = async () => prepareForExport()

        const pngs = await prepareForExport()

        if (cancelled) return
        ;(window as any).getChartExportPng = async () => pngs

        emitChartReady({ requestId, outputCount: pngs.length })
      } catch (error) {
        if (cancelled) return

        onError?.(error as Error)

        emitChartReady({ requestId, error: (error as Error).message })
      }
    })

    return () => {
      cancelled = true
      cancelAnimationFrame(handle)
      if ((window as any).getChartExportPng) delete (window as any).getChartExportPng
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [requestId, depsReady])
}
